import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { CARD_FIELD_DEFS } from "@/lib/orgUtils";
import { X, GripVertical, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { EmployeeCard } from "./EmployeeNode";
import { CoAuthorPicker } from "./CoAuthorPicker";

const SAMPLE = { employee_id: "SZ-000", full_name: "Priya Nair", designation: "Chief People Officer", department: "People & Culture", date_of_joining: "2021-06-15", email: "priya@acme.com", location: "Mumbai", employment_type: "Permanent" };

export function ViewSettingsDialog({ view, employees, onClose, onSaved, onDeleted, isNew, currentUser }) {
  const [name, setName] = useState(view?.name || "New view");
  const [root, setRoot] = useState(view?.root_employee_id || "");
  const [fields, setFields] = useState(view?.display_config?.fields || ["designation"]);
  const [opts, setOpts] = useState(view?.display_config?.options || { doj_render: "tenure", department_render: "tag" });
  const [style, setStyle] = useState(view?.card_style || { show_photo: true, size: "M", show_report_count: true });
  const [colorCfg, setColorCfg] = useState(view?.color_config || { enabled: true, rule: "tenure", placement: "edge", bands: [] });
  const [published, setPublished] = useState(view?.is_published || false);
  const [coAuthors, setCoAuthors] = useState(view?.co_authors || []);
  const [allUsers, setAllUsers] = useState([]);
  const canManageAccess = isNew || view?.can_manage_access;

  useEffect(() => {
    if (canManageAccess) api.get("/users/basic").then((r) => setAllUsers(r.data)).catch(() => {});
  }, [canManageAccess]);

  const customKeys = [...new Set(employees.flatMap((e) => Object.keys(e.custom_fields || {})))];
  const allFields = [...CARD_FIELD_DEFS, ...customKeys.map((k) => ({ key: `custom:${k}`, label: k }))];

  const toggleField = (key) =>
    setFields((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));

  const moveField = (key, dir) =>
    setFields((f) => {
      const i = f.indexOf(key);
      const j = i + dir;
      if (j < 0 || j >= f.length) return f;
      const next = [...f];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    const payload = {
      name, root_employee_id: root || null,
      display_config: { fields, options: opts },
      card_style: style, color_config: colorCfg, is_published: published,
      default_layout: view?.default_layout || "classic",
    };
    if (canManageAccess) payload.co_authors = coAuthors;
    try {
      const res = isNew ? await api.post("/views", payload) : await api.put(`/views/${view.id}`, payload);
      if (isNew && coAuthors.length) await api.put(`/views/${res.data.id}`, { co_authors: coAuthors });
      toast.success(isNew ? "View created" : "View saved");
      onSaved(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteView = async () => {
    if (!window.confirm(`Delete the chart view "${view.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/views/${view.id}`);
      toast.success("View deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const preview = { ...view, display_config: { fields, options: opts }, card_style: style, color_config: colorCfg };

  return (
    <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="view-settings-dialog">
        <div className="flex items-center justify-between p-5 border-b border-[#181B34]/10 sticky top-0 bg-white z-10">
          <h3 className="text-xl font-black tracking-tight">{isNew ? "Create chart view" : "View settings"}</h3>
          <button data-testid="close-view-settings" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F0F3FF] flex items-center justify-center"><X size={17} /></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">View name</label>
              <input data-testid="view-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Root (subtree)</label>
              <select data-testid="view-root-select" value={root} onChange={(e) => setRoot(e.target.value)}
                className="mt-1.5 w-full h-10 px-2 rounded-lg border border-[#181B34]/15 font-semibold bg-white">
                <option value="">Whole organization</option>
                {employees.filter((e) => e.status === "active").map((e) => (
                  <option key={e.id} value={e.id}>{e.full_name} — {e.designation}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Card fields</label>
              <div className="mt-1.5 space-y-1">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#F6F7FB] text-sm font-bold text-[#9699AD]">Full Name <span className="ml-auto text-[10px]">always on</span></div>
                {allFields.map((f) => {
                  const on = fields.includes(f.key);
                  return (
                    <div key={f.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm font-semibold ${on ? "bg-[#6161FF]/8" : ""}`}>
                      <GripVertical size={13} className="text-[#C4C7D4]" />
                      <span>{f.label}</span>
                      <div className="ml-auto flex items-center gap-1.5">
                        {on && <>
                          <button onClick={() => moveField(f.key, -1)} className="text-xs font-black text-[#676a82] hover:text-[#6161FF] px-1">↑</button>
                          <button onClick={() => moveField(f.key, 1)} className="text-xs font-black text-[#676a82] hover:text-[#6161FF] px-1">↓</button>
                        </>}
                        <Switch data-testid={`field-toggle-${f.key}`} checked={on} onCheckedChange={() => toggleField(f.key)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {fields.includes("date_of_joining") && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">DOJ renders as</label>
                <div className="mt-1.5 flex gap-1.5">
                  {["date", "tenure", "both"].map((m) => (
                    <button key={m} onClick={() => setOpts({ ...opts, doj_render: m })}
                      className={`px-3 h-8 rounded-full text-xs font-bold capitalize ${opts.doj_render === m ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{m}</button>
                  ))}
                </div>
              </div>
            )}
            {fields.includes("department") && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Department renders as</label>
                <div className="mt-1.5 flex gap-1.5">
                  {["text", "tag"].map((m) => (
                    <button key={m} onClick={() => setOpts({ ...opts, department_render: m })}
                      className={`px-3 h-8 rounded-full text-xs font-bold capitalize ${opts.department_render === m ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{m}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Live preview</label>
              <div className="mt-2 p-5 rounded-xl bg-[#F6F7FB] flex justify-center organa-grain">
                <EmployeeCard emp={SAMPLE} config={preview} childCount={4} />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl border border-[#181B34]/10">
              <span className="text-sm font-bold">Show photo</span>
              <Switch data-testid="toggle-show-photo" checked={style.show_photo !== false} onCheckedChange={(v) => setStyle({ ...style, show_photo: v })} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl border border-[#181B34]/10">
              <span className="text-sm font-bold">Direct-report count badge</span>
              <Switch data-testid="toggle-report-count" checked={style.show_report_count !== false} onCheckedChange={(v) => setStyle({ ...style, show_report_count: v })} />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl border border-[#181B34]/10">
              <span className="text-sm font-bold">Color by tenure</span>
              <Switch data-testid="toggle-tenure-color" checked={!!colorCfg.enabled} onCheckedChange={(v) => setColorCfg({ ...colorCfg, enabled: v })} />
            </div>
            {colorCfg.enabled && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#676a82]">Color placement</label>
                <div className="mt-1.5 flex gap-1.5">
                  {[["edge", "Edge strip"], ["header", "Header band"], ["full", "Full card"]].map(([v, l]) => (
                    <button key={v} onClick={() => setColorCfg({ ...colorCfg, placement: v })}
                      className={`px-3 h-8 rounded-full text-xs font-bold ${colorCfg.placement === v ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#4b4e63]"}`}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between p-3 rounded-xl border border-[#181B34]/10 bg-[#00C875]/5">
              <div>
                <span className="text-sm font-bold">Published</span>
                <p className="text-xs text-[#676a82]">Viewers can only see published views</p>
              </div>
              <Switch data-testid="toggle-published" checked={published} onCheckedChange={setPublished} />
            </div>
            {canManageAccess && (
              <CoAuthorPicker allUsers={allUsers} coAuthors={coAuthors} setCoAuthors={setCoAuthors}
                currentUser={currentUser} view={view} isNew={isNew} />
            )}
          </div>
        </div>
        <div className="p-5 border-t border-[#181B34]/10 flex items-center gap-2 sticky bottom-0 bg-white">
          {!isNew && (
            <button data-testid="delete-view-btn" onClick={deleteView}
              className="flex items-center gap-1.5 px-3 h-10 rounded-xl font-bold text-[#E2445C] hover:bg-[#E2445C]/10 text-sm">
              <Trash2 size={14} /> Delete view
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="px-5 h-10 rounded-xl font-bold text-[#4b4e63] hover:bg-[#F0F3FF]">Cancel</button>
            <button data-testid="save-view-btn" onClick={save} className="px-6 h-10 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold">
              {isNew ? "Create view" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
